import React, { useState, useEffect } from "react";
import { t } from "i18next";
import { getUserInvoices } from "../../../api/invoices";
import getUserTransactions from "../../../api/getUserTransactions";
import { getUserInfo } from "../../../api/usercall";
import EmptyState from "../../../components/emptystate/EmptyState";
import Loading from "../../../common/components/Loading";

const TABS = [
    { id: "invoices", labelKey: "invoices" },
    { id: "transactions", labelKey: "transactions" },
];

function Accounting({ setActiveTab }) {
    const [activeTab, setActiveTabLocal] = useState("invoices");
    const [invoices, setInvoices] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [balance, setBalance] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const [invoicesData, transactionsData, userData] = await Promise.all([
                    getUserInvoices().catch(() => []),
                    getUserTransactions().catch(() => ({ transactions: [] })),
                    getUserInfo().catch(() => ({ user: null })),
                ]);
                setInvoices(Array.isArray(invoicesData) ? invoicesData : (invoicesData.invoices || []));
                setTransactions((transactionsData.transactions || []));
                setBalance(userData?.user?.balance ?? 0);
            } catch (err) {
                setError(err.message || "Ошибка загрузки данных");
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const formatDate = (isoString) => {
        if (!isoString) return "—";
        const date = new Date(isoString);
        return date.toLocaleDateString("ru-RU", { day: "2-digit", month: "short", year: "numeric" });
    };

    const formatAmount = (amount) => {
        const num = Number(amount);
        return Number.isFinite(num) ? num.toLocaleString("ru-RU") : "—";
    };

    const getInvoiceStatusBadge = (status) => {
        const badges = {
            DRAFT: { bg: "bg-gray-100", text: "text-gray-600", label: t('draft') || 'Черновик' },
            SENT: { bg: "bg-blue-100", text: "text-blue-700", label: t('sent') || 'Отправлен' },
            PAID: { bg: "bg-green-100", text: "text-green-700", label: t('paid') || 'Оплачен' },
            CANCELLED: { bg: "bg-red-100", text: "text-red-700", label: t('cancelled') || 'Отменен' },
            OVERDUE: { bg: "bg-orange-100", text: "text-orange-700", label: t('overdue') || 'Просрочен' },
        };
        const badge = badges[status] || { bg: "bg-gray-100", text: "text-gray-600", label: status };
        return (
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${badge.bg} ${badge.text}`}>
                {badge.label}
            </span>
        );
    };

    const getTransactionIcon = (type) => {
        const isPositive = type !== "refund" && type !== "debit";
        return (
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
                isPositive ? "bg-green-50" : "bg-red-50"
            }`}>
                {isPositive ? (
                    <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                ) : (
                    <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h2m14 0h2M7 17l3-8 3 8M7 7l3 8 3-8" />
                    </svg>
                )}
            </div>
        );
    };

    return (
        <div className="w-full max-w-2xl mx-auto">
            {/* Balance Card */}
            <div className="relative overflow-hidden bg-gradient-to-br from-[#147F26] via-[#0f6820] to-[#0a4d16] rounded-3xl p-6 mb-6 shadow-xl shadow-[#147F26]/20">
                {/* Decorative circles */}
                <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-white/10" />
                <div className="absolute -bottom-6 -left-6 w-32 h-32 rounded-full bg-white/5" />

                <div className="relative flex items-center justify-between">
                    <div>
                        <p className="text-white/70 text-sm font-medium mb-1">{t('totalBalance') || 'Жалпы баланс'}</p>
                        <p className="text-4xl font-bold text-white mb-1">
                            {loading ? (
                                <span className="opacity-50">...</span>
                            ) : (
                                <>
                                    {formatAmount(balance)}
                                    <span className="text-xl font-normal ml-1">₸</span>
                                </>
                            )}
                        </p>
                        <p className="text-white/50 text-xs">{t('availableFunds') || 'Қолда бар қаржы'}</p>
                    </div>
                    <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
                        <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                        </svg>
                    </div>
                </div>

                {/* Quick Stats */}
                <div className="relative flex gap-6 mt-5 pt-5 border-t border-white/10">
                    <div>
                        <p className="text-white/50 text-xs">{t('invoices') || 'Счёттар'}</p>
                        <p className="text-white font-bold text-lg">{invoices.length}</p>
                    </div>
                    <div>
                        <p className="text-white/50 text-xs">{t('transactions') || 'Транзакциялар'}</p>
                        <p className="text-white font-bold text-lg">{transactions.length}</p>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex bg-gray-50 rounded-2xl p-1 mb-6">
                {TABS.map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTabLocal(tab.id)}
                        className={`flex-1 py-3 px-4 rounded-xl text-sm font-semibold transition-all duration-200 ${
                            activeTab === tab.id
                                ? "bg-white text-gray-900 shadow-sm"
                                : "text-gray-500 hover:text-gray-700"
                        }`}
                    >
                        {t(tab.labelKey)}
                    </button>
                ))}
            </div>

            {/* Content */}
            {loading && (
                <div className="flex justify-center py-12">
                    <Loading />
                </div>
            )}

            {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-2xl">
                    <p className="text-sm text-red-600">{error}</p>
                </div>
            )}

            {!loading && !error && activeTab === "invoices" && (
                <div className="space-y-3">
                    {invoices.length === 0 ? (
                        <div className="bg-white rounded-2xl p-8 text-center">
                            <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                            </div>
                            <p className="text-gray-500 font-medium">{t('noInvoices') || 'Счёттар жоқ'}</p>
                            <p className="text-gray-400 text-sm mt-1">{t('noInvoicesDesc') || 'Сізде бірде-бір счёт жоқ'}</p>
                        </div>
                    ) : (
                        invoices.map((invoice) => (
                            <div key={invoice.id} className="bg-white rounded-2xl p-5 border border-gray-100 hover:shadow-md hover:border-gray-200 transition-all duration-200">
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-12 h-12 rounded-2xl bg-blue-50 flex items-center justify-center">
                                            <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <p className="font-bold text-gray-900">{invoice.invoice_number || `Счёт #${invoice.id}`}</p>
                                            <p className="text-xs text-gray-400">{formatDate(invoice.created_at)}</p>
                                        </div>
                                    </div>
                                    {getInvoiceStatusBadge(invoice.status)}
                                </div>

                                <div className="flex items-center justify-between">
                                    <p className="text-sm text-gray-500">
                                        {invoice.order_id ? `Заказ #${invoice.order_id}` : t('invoice') || 'Счёт'}
                                    </p>
                                    <p className="text-xl font-bold text-gray-900">
                                        {formatAmount(invoice.amount)} ₸
                                    </p>
                                </div>

                                {invoice.payment_link && invoice.status !== "PAID" && (
                                    <a
                                        href={invoice.payment_link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="mt-4 flex items-center justify-center gap-2 w-full py-3 bg-[#147F26] hover:bg-[#0f6820] text-white text-sm font-semibold rounded-xl transition-colors"
                                    >
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                        </svg>
                                        {t('payOnline') || 'Төлемеу'}
                                    </a>
                                )}
                            </div>
                        ))
                    )}
                </div>
            )}

            {!loading && !error && activeTab === "transactions" && (
                <div className="space-y-3">
                    {transactions.length === 0 ? (
                        <div className="bg-white rounded-2xl p-8 text-center">
                            <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h2m14 0h2M7 17l3-8 3 8M7 7l3 8 3-8" />
                                </svg>
                            </div>
                            <p className="text-gray-500 font-medium">{t('noTransactions') || 'Транзакциялар жоқ'}</p>
                            <p className="text-gray-400 text-sm mt-1">{t('noTransactionsDesc') || 'Сізде бірде-бір транзакция жоқ'}</p>
                        </div>
                    ) : (
                        transactions.map((tx) => {
                            const isPositive = tx.transaction_type !== "refund" && tx.transaction_type !== "debit";
                            return (
                                <div key={tx.id} className="bg-white rounded-2xl p-5 border border-gray-100 hover:shadow-md hover:border-gray-200 transition-all duration-200">
                                    <div className="flex items-center gap-4">
                                        {getTransactionIcon(tx.transaction_type)}
                                        <div className="flex-1 min-w-0">
                                            <p className="font-semibold text-gray-900 truncate">
                                                {tx.announcement?.title || t(tx.transaction_type) || tx.transaction_type}
                                            </p>
                                            <p className="text-xs text-gray-400 mt-0.5">{formatDate(tx.created_at)}</p>
                                        </div>
                                        <div className="text-right">
                                            <p className={`text-lg font-bold ${
                                                isPositive ? "text-green-600" : "text-red-500"
                                            }`}>
                                                {isPositive ? "+" : "−"}
                                                {formatAmount(tx.amount)} ₸
                                            </p>
                                            {tx.payment && (
                                                <p className="text-xs text-gray-400 mt-0.5">
                                                    {tx.payment.currency}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            )}
        </div>
    );
}

export default Accounting;
